import { HOST_TO_GUEST, GUEST_TO_HOST, PROTOCOL_VERSION } from './protocol';

/**
 * Source for `window.apibooSDK`. It is the first data element of the
 * sandboxed guest iframe's srcdoc (see guestDocument.js), ahead of the
 * extension's own entry script. This runs in the guest's opaque-origin realm — it cannot import
 * anything from the host bundle, so the protocol constants are inlined as a
 * JSON literal (kept in sync with sdk/protocol.js at generation time, not by
 * hand) rather than duplicated by hand.
 *
 * Deliberately plain ES2017, no JSX/imports — this is never processed by
 * CRA/babel, it runs as-is inside the guest.
 */
export function buildGuestSdkSource(frameToken) {
  if (typeof frameToken !== 'string' || frameToken.length === 0) {
    // Refuse to build an unauthenticated guest realm rather than silently
    // producing one that accepts messages from any frame. See newFrameToken().
    throw new Error('buildGuestSdkSource requires a frameToken');
  }
  const proto = JSON.stringify({ HOST_TO_GUEST, GUEST_TO_HOST, PROTOCOL_VERSION });
  const token = JSON.stringify(frameToken);

  return `
(function () {
  var PROTO = ${proto};
  var H2G = PROTO.HOST_TO_GUEST;
  var G2H = PROTO.GUEST_TO_HOST;

  // The host document that created this iframe, captured once at load, plus
  // the per-mount secret it inlined here. Both are used to authenticate every
  // inbound message — see the listener below.
  var HOST = window.parent;
  var TOKEN = ${token};

  var lifecycleHandlers = { activate: null, freeze: null, resume: null, dispose: null };
  var initialized = false;
  var pendingInit = null;
  var readyTimer = null;
  var treeHandlers = { onExpand: null, onAction: null, onNodeClick: null, onSectionAction: null, onConnectionsChanged: null };
  var pendingCalls = {};
  var callSeq = 0;

  function post(type, payload) {
    HOST.postMessage({ type: type, payload: payload, ts: Date.now(), token: TOKEN }, '*');
  }

  function callHost(method, params) {
    callSeq += 1;
    var callId = 'guest-call-' + callSeq + '-' + Date.now();
    return new Promise(function (resolve, reject) {
      pendingCalls[callId] = { resolve: resolve, reject: reject };
      post(G2H.RPC_CALL, { callId: callId, method: method, params: params || {} });
    });
  }

  window.addEventListener('message', function (event) {
    // ── Sender authentication ────────────────────────────────────────────
    // postMessage target origins have to be '*' for an opaque-origin sandbox,
    // so the ONLY possible authentication is the receiver checking who sent
    // it. Two independent controls, both required:
    //
    //  (1) event.source. The browser sets it to the posting realm's
    //      WindowProxy and script cannot override it. ExtensionHostLayer
    //      mounts every activated extension panel's iframe in the same host
    //      document, so every OTHER extension — and the pre-request script
    //      sandbox — is a SIBLING, reachable as parent.frames[i]. A sibling's
    //      messages arrive with event.source === that sibling, never === the
    //      host window, so this rejects them. WindowProxy identity
    //      comparison needs no same-origin access, so it works from inside
    //      this sandbox.
    //
    //  (2) The per-mount token. It covers what (1) cannot: if a future layout
    //      ever puts a wrapper frame between host and guest, window.parent
    //      stops being the host and (1) would authenticate the wrapper. No
    //      other guest can learn the token — it is inlined into THIS iframe's
    //      srcdoc, which lives in the host document, and a sandboxed guest
    //      without allow-same-origin cannot read the host DOM.
    //
    // Neither check alone is treated as sufficient; a message must pass both.
    if (event.source !== HOST) return;
    var data = event.data;
    if (!data || typeof data.type !== 'string') return;
    if (data.token !== TOKEN) return;

    if (data.type === H2G.INIT) {
      // Once only: READY is repeated until INIT arrives (see the bottom), so a
      // second INIT can be in flight — it must not activate the extension twice.
      if (initialized) return;
      initialized = true;
      if (readyTimer) clearInterval(readyTimer);
      if (typeof lifecycleHandlers.activate === 'function') {
        try { lifecycleHandlers.activate(data.payload || {}); } catch (e) { reportError(e); }
      } else {
        // The SDK and the extension's entry run as two separate blob scripts
        // (extension-guest-boot.js). INIT can land between them — before the
        // entry has called onActivate — and was then dropped: the extension
        // never started (the DB Manager sidebar stayed on "No connections
        // yet", ~1 load in 4, 30.09.2026). Keep it for onActivate to replay.
        pendingInit = data.payload || {};
      }
      return;
    }
    if (data.type === H2G.FREEZE) {
      if (typeof lifecycleHandlers.freeze === 'function') {
        try { lifecycleHandlers.freeze(); } catch (e) { reportError(e); }
      }
      return;
    }
    if (data.type === H2G.RESUME) {
      if (typeof lifecycleHandlers.resume === 'function') {
        try { lifecycleHandlers.resume(); } catch (e) { reportError(e); }
      }
      return;
    }
    if (data.type === H2G.DISPOSE) {
      Promise.resolve()
        .then(function () {
          if (typeof lifecycleHandlers.dispose === 'function') return lifecycleHandlers.dispose();
        })
        .catch(reportError)
        .then(function () { post(G2H.DISPOSED, {}); });
      return;
    }
    if (data.type === H2G.RPC_RESULT) {
      var entry = pendingCalls[data.payload.callId];
      if (!entry) return;
      delete pendingCalls[data.payload.callId];
      if (data.payload.error) entry.reject(new Error(data.payload.error));
      else entry.resolve(data.payload.result);
      return;
    }
    if (data.type === '__db_connections_changed__') {
      if (typeof treeHandlers.onConnectionsChanged === 'function') treeHandlers.onConnectionsChanged();
      return;
    }
    if (data.type === '__tree_expand__') {
      if (typeof treeHandlers.onExpand === 'function') treeHandlers.onExpand(data.payload);
      return;
    }
    if (data.type === '__tree_action__') {
      if (typeof treeHandlers.onAction === 'function') treeHandlers.onAction(data.payload);
      return;
    }
    if (data.type === '__tree_node_click__') {
      if (typeof treeHandlers.onNodeClick === 'function') treeHandlers.onNodeClick(data.payload);
      return;
    }
    if (data.type === '__section_action__') {
      if (typeof treeHandlers.onSectionAction === 'function') treeHandlers.onSectionAction(data.payload);
      return;
    }
  });

  function reportError(err) {
    post(G2H.ERROR, { message: err && err.message ? err.message : String(err) });
  }

  window.apibooSDK = {
    version: PROTO.PROTOCOL_VERSION,

    onActivate: function (fn) {
      lifecycleHandlers.activate = fn;
      if (pendingInit && typeof fn === 'function') {
        var payload = pendingInit;
        pendingInit = null;
        try { fn(payload); } catch (e) { reportError(e); }
      }
    },
    onFreeze: function (fn) { lifecycleHandlers.freeze = fn; },
    onResume: function (fn) { lifecycleHandlers.resume = fn; },
    onDispose: function (fn) { lifecycleHandlers.dispose = fn; },

    tree: {
      publish: function (sectionId, rootNodes) {
        post(G2H.TREE_PUBLISH, { sectionId: sectionId, nodes: rootNodes });
      },
      // Open nodes in the host tree (e.g. a connection down to its tables
      // after the user connects it). The host loads lazy levels as they appear.
      expand: function (sectionId, nodeIds) {
        post(G2H.TREE_EXPAND, { sectionId: sectionId, nodeIds: nodeIds });
      },
      onExpand: function (fn) { treeHandlers.onExpand = fn; },
      onAction: function (fn) { treeHandlers.onAction = fn; },
      onNodeClick: function (fn) { treeHandlers.onNodeClick = fn; },
      onConnectionsChanged: function (fn) { treeHandlers.onConnectionsChanged = fn; },
      onSectionAction: function (fn) { treeHandlers.onSectionAction = fn; },
    },

    call: function (method, params) { return callHost(method, params); },

    storage: {
      get: function (key) { return callHost('storage.get', { key: key }); },
      set: function (key, value) { return callHost('storage.set', { key: key, value: value }); },
    },

    ui: {
      openModal: function (spec) { return callHost('ui.openModal', spec); },
      openTable: function (spec) { return callHost('ui.openTable', spec); },
      openQuery: function (spec) { return callHost('ui.openQuery', spec); },
      openOverview: function (spec) { return callHost('ui.openOverview', spec); },
      refreshTable: function (spec) { return callHost('ui.refreshTable', spec); },
      notify: function (spec) { return callHost('ui.notify', spec); },
    },
  };

  // Back-compat alias from the pre-rebrand SDK name.
  window.booreqSDK = window.apibooSDK;

  // READY is repeated until INIT arrives (defensive): the host starts
  // listening in an effect that runs AFTER this frame is in the page, so a
  // single READY could in principle land before anyone listens. Every 100 ms,
  // for at most 15 s. (The lost start seen on 30.09.2026 was INIT arriving
  // before onActivate — see pendingInit above.)
  post(G2H.READY, { version: PROTO.PROTOCOL_VERSION });
  var readyTries = 0;
  readyTimer = setInterval(function () {
    readyTries += 1;
    if (initialized || readyTries > 150) { clearInterval(readyTimer); return; }
    post(G2H.READY, { version: PROTO.PROTOCOL_VERSION });
  }, 100);
})();
`;
}
